import { useEffect, useRef } from 'react'
import { useArcVoiceCall } from '../../contexts/ArcVoiceCallContext'
import { ArcVoiceCallStatus } from '../../helpers/arcVoice/arcVoiceCall'
import { listApprovals } from '../../redux/atlasai/actions'
import { useDispatch } from '../../redux/hooks'
import PendingApprovals from '../Arc/PendingApprovals'
import styles from './ArcVoice.module.scss'

/** How far a server timestamp may trail the browser's clock and still count as on the call. */
const CLOCK_SKEW_MS = 5000

/** Space kept between the card and Arc's activity rail stacked above it. */
const DOCK_GAP_PX = 8

/** Publishes how high the docked card reaches as `--arc-dock-height`, so the rail can sit above it. */
const useDockHeight = (isOpen: boolean) => {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const node = ref.current
    const root = document.documentElement
    if (!isOpen || !node) return undefined
    const update = () => {
      const { top } = node.getBoundingClientRect()
      const height = node.childElementCount ? window.innerHeight - top + DOCK_GAP_PX : 0
      root.style.setProperty('--arc-dock-height', `${Math.max(0, height)}px`)
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(node)
    return () => {
      observer.disconnect()
      root.style.removeProperty('--arc-dock-height')
    }
  }, [isOpen])
  return ref
}

/**
 * What Arc needs your OK for while you're on a call, floating over whatever you
 * are looking at — the full call, the small one in the AIM panel, or another
 * page entirely — so approving never means going looking for the conversation.
 * Only cards raised since the call began; they arrive live over APPROVAL_CREATED.
 */
const ArcCallApprovals = () => {
  const { snapshot, callChatSessionId } = useArcVoiceCall()
  const dispatch = useDispatch()
  const callChannelId = snapshot?.channelId ?? null
  const isOpen = !!snapshot && snapshot.status !== ArcVoiceCallStatus.Ended
  const dockRef = useDockHeight(isOpen)

  // Once per call: a card raised before a reload is otherwise only in the chat.
  useEffect(() => {
    if (callChannelId) void dispatch(listApprovals({}))
  }, [callChannelId, dispatch])

  if (!snapshot || !isOpen) return null
  return (
    <div
      ref={dockRef}
      className={styles.callApprovals}
      role="region"
      aria-label="Waiting on your approval"
    >
      {/* The call's own chat, when on screen, already shows its cards above its composer. */}
      <PendingApprovals
        createdSince={snapshot.startedAt - CLOCK_SKEW_MS}
        excludeSessionId={callChatSessionId}
      />
    </div>
  )
}

export default ArcCallApprovals
